import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { downloadLogs, getLogs } from '../../../api/endpoints/logs';
import type { LogFilters } from '../../../api/types/logs';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { saveBlob } from '../../../components/util/blob';

/** Legacy `Logs/Index` — provider/operation/level + date-range filtered session log list. */
export default function LogsListPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [provider, setProvider] = useState('');
  const [operation, setOperation] = useState('');
  const [level, setLevel] = useState('');
  const [start, setStart] = useState<Dayjs | null>(dayjs().subtract(1, 'day'));
  const [end, setEnd] = useState<Dayjs | null>(dayjs());

  const filters = (): LogFilters => ({
    provider: provider || undefined,
    operation: operation || undefined,
    level: level || undefined,
    startDate: start?.format('YYYY-MM-DD'),
    endDate: end?.format('YYYY-MM-DD'),
  });

  const search = useMutation({ mutationFn: () => getLogs(filters()) });
  const download = useMutation({
    mutationFn: () => downloadLogs(filters()),
    onSuccess: (res) => saveBlob(res.blob, res.filename ?? 'loglar.json'),
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İndirme başarısız.', { variant: 'error' }),
  });

  const rows = search.data?.logs ?? [];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Sistem Logları
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField size="small" label="Sağlayıcı" value={provider} onChange={(e) => setProvider(e.target.value)} />
          <TextField size="small" label="İşlem" value={operation} onChange={(e) => setOperation(e.target.value)} />
          <TextField size="small" label="Seviye" value={level} onChange={(e) => setLevel(e.target.value)} />
          <DatePicker label="Başlangıç" value={start} onChange={setStart} slotProps={{ textField: { size: 'small' } }} />
          <DatePicker label="Bitiş" value={end} onChange={setEnd} slotProps={{ textField: { size: 'small' } }} />
          <Button variant="contained" onClick={() => search.mutate()} disabled={search.isPending}>
            {search.isPending ? 'Aranıyor…' : 'Ara'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => download.mutate()}
            disabled={download.isPending}
          >
            İndir
          </Button>
        </Stack>
        <ApiErrorAlert error={search.error} sx={{ mt: 2 }} />
      </Paper>

      {search.isPending && <CircularProgress size={24} />}
      {search.isSuccess && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Sağlayıcı</TableCell>
                <TableCell>İşlem</TableCell>
                <TableCell>Seviye</TableCell>
                <TableCell>Mesaj</TableCell>
                <TableCell>Session</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Kayıt bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={row.id ?? `${row.sessionId ?? 'row'}-${index}`} hover>
                  <TableCell>{row.logged ?? '—'}</TableCell>
                  <TableCell>{row.provider ?? '—'}</TableCell>
                  <TableCell>{row.operation ?? '—'}</TableCell>
                  <TableCell>{row.level ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.message ?? '—'}
                  </TableCell>
                  <TableCell>
                    {row.sessionId ? (
                      <Link component={RouterLink} to={`/logs/${encodeURIComponent(row.sessionId)}`}>
                        {row.sessionId}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
